<template>
  <RouterLink
    :to="`/@${invitee.username}?back=history`"
    :class="['attendee-row', { 'is-blocked': invitee.isBlocked, 'is-even': (order || 0) % 2 === 0 }]"
  >
    <div class="info">
      <img v-if="invitee.profileImageBlobHash" class="picture" :src="getImageSrc" />
      <div v-else class="picture missing">
        <GamIcon :name="GamIconName.PAWN_EMPTY" :size="GamIconSize.SMALL" />
      </div>

      <div class="text">
        <span v-if="invitee.username" class="username">@{{ invitee.username }}</span>
        <span v-if="!invitee.username" class="username">{{ invitee.username }}</span>
        <span class="status">
          Invited
          <template v-if="invitee.id === user?.id">(You)</template>
        </span>
      </div>
    </div>

    <div class="icons">
      <div class="chips">
        <GamChip
          v-if="typeof invitee.rating === 'number'"
          class="rating"
          :left-icon="GamIconName.GAMBIT"
          :dots-rating="isDots ? invitee.rating : undefined"
          :label="isDots ? undefined : String(invitee.rating)"
          is-upper-case
        />
      </div>
    </div>
  </RouterLink>
</template>

<script setup lang="ts">
import type { InviteeDto } from '@/core/data/user/user.interface';
import { RatingDisplayMode } from '@/core/data/user/user.type';
import { gambits } from '@/core/gambits';
import { AppEndpoint } from '@/core/network/api/constants/api.url.constant';
import { useAuthStore } from '@/stores/auth.store';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import GamChip from '../GamChip.vue';
import GamIcon from '../GamIcon.vue';
interface InviteeProps {
  invitee: InviteeDto;
  isWithdrawn?: boolean;
  isDeclined?: boolean;
  isWaiting?: boolean;
  order?: number;
}

const props = withDefaults(defineProps<InviteeProps>(), {
  chessSetCount: 0,
  order: 0,
});

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const isDots = computed(() => props.invitee?.ratingDisplayMode === RatingDisplayMode.DOTS);

const getImageSrc = computed((): string => {
  let url = `${gambits.configService.getServiceRoutingHost()}${AppEndpoint.BLOB}`;
  url = url.replace(new RegExp('{blobHash}', 'g'), props.invitee?.profileImageBlobHash || '');
  return `${url}?width=100`;
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/partial';

@keyframes attention {
  0% {
    filter: none;
  }

  33% {
    filter: none;
  }

  50% {
    filter: saturate(0%) brightness(200%);
  }

  66% {
    filter: none;
  }

  100% {
    filter: none;
  }
}

.attendee-row {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  width: 100%;
  border-radius: 56px !important;
  padding: 4px 16px 4px 4px;
  gap: 1rem;

  text-decoration: none;
  color: var(--color-white-100);
}

.attendee-row.is-handshake {
  width: calc(100% - 56px - var(--spacing-xs));
}

.attendee-row:not(.is-blocked) {
  @extend %tinted-card;
}

.attendee-row.is-blocked {
  cursor: unset;
  @extend %disabled-card;
  opacity: 0.5;
}

.info {
  display: flex;
  align-items: center;
  justify-content: stretch;
  flex-grow: 1;
  gap: 0.75rem;
  overflow: hidden;
}

.icons {
  display: flex;
  align-items: center;
  justify-content: stretch;
  flex-shrink: 1;
  gap: 0.75rem;
}

.picture {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  object-fit: cover;
  overflow: hidden;
  border-radius: 48px !important;
  filter: saturate(0);

  &.missing {
    // background: var(--color-linear-gradient);
    background: var(--color-dark-70);
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.text {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  overflow: hidden;
  transform: translateY(-1.5px);
}

.text > * {
  line-height: 21px;
}

.checked-in {
  margin-left: -0.125rem;
  animation: attention 2000ms infinite ease-in-out;
  animation-fill-mode: backwards;
}

// .is-even .checked-in {
//   animation-delay: 1000ms;
// }

.username {
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  letter-spacing: 0.03em;
  font-size: 16px;
}

.current-user {
  color: var(--color-white-30);
  font-weight: 300;
  letter-spacing: 0.01em;
}

.status {
  color: var(--color-white-30);
  font-weight: 400;
  letter-spacing: 0.01em;
}

.chess-set-count {
  display: flex;
  align-items: center;
}

.chips,
.chips .roles {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.is-blocked {
  // opacity: 0.5;
  pointer-events: none;
}
</style>
